@if (snapshot$ | async; as s) {
  @if (s.status !== 'idle' && s.item) {
    <div class="audio-panel" role="region" aria-label="Audio player">
      <!-- Gaps live in the SCSS, not utility classes: the utilities' !important would
           beat the narrow-viewport overrides below 992px/768px. -->
      <div class="panel-body d-flex align-items-center justify-content-space-between">
        <!-- The identity block doubles as "open the text": the iOS full player's
             Open Text action, folded into the bar. -->
        <button
          class="panel-identity d-flex align-items-center gap-12 min-w-0 cursor-pointer"
          (click)="openText(s)"
          title="Open text"
        >
          <div class="media-thumb">
            @if (s.item.logoUrl) {
              <img [src]="s.item.logoUrl" alt="" />
            } @else {
              <i class="ri-headphone-line"></i>
            }
          </div>
          <div class="d-flex flex-column min-w-0 text-start">
            <span class="identity-title fs-14 fw-medium text-truncate" [title]="s.item.title ?? ''">
              {{ s.item.title }}
            </span>
            @if (s.status === 'error') {
              <span class="identity-sub fs-12 error-text text-truncate">
                {{ errorLabel(s.error) }}
              </span>
            } @else if (s.partsCount > 1) {
              <span class="identity-sub fs-12 muted-color"
                >Part {{ s.partIndex + 1 }} of {{ s.partsCount }}</span
              >
            }
          </div>
        </button>

        <div class="panel-center d-flex flex-column min-w-0">
          <div class="transport d-flex align-items-center justify-content-center">
            <button
              class="skip-button"
              (click)="player.skipBackward()"
              [title]="'Back ' + skipBackSeconds + ' seconds'"
              [attr.aria-label]="'Back ' + skipBackSeconds + ' seconds'"
            >
              <app-icon name="rewind-15-back" [size]="20" />
            </button>
            <button
              class="play-button"
              (click)="togglePlay(s)"
              [attr.aria-label]="s.status === 'playing' ? 'Pause' : 'Play'"
            >
              @if (s.status === 'loading') {
                <i class="ri-loader-4-line spinning"></i>
              } @else if (s.status === 'playing') {
                <app-icon name="pause" [size]="18" />
              } @else if (s.status === 'error') {
                <i class="ri-restart-line"></i>
              } @else {
                <app-icon name="play" [size]="18" />
              }
            </button>
            <button
              class="skip-button"
              (click)="player.skipForward()"
              [title]="'Forward ' + skipForwardSeconds + ' seconds'"
              [attr.aria-label]="'Forward ' + skipForwardSeconds + ' seconds'"
            >
              <app-icon name="rewind-30-forward" [size]="20" />
            </button>
          </div>
          <div class="progress-row d-flex align-items-center">
            <span class="time-label">{{ formatTime(s.currentTime) }}</span>
            <input
              class="seek-bar flex-grow-1"
              type="range"
              min="0"
              [max]="s.duration ?? 0"
              step="0.1"
              [value]="seekValue(s, seekBar.value)"
              [style.--played.%]="playedPercent(s, seekBar.value)"
              (pointerdown)="seekStart()"
              (change)="seekCommit(seekBar.value)"
              (pointerup)="seekRelease(seekBar.value)"
              (pointercancel)="seekCancel()"
              #seekBar
              aria-label="Seek"
            />
            <span class="time-label">
              {{ s.duration && s.durationIsEstimate ? '~' : '' }}{{ formatTime(s.duration) }}
            </span>
          </div>
        </div>

        <div class="panel-actions d-flex align-items-center">
          @if (s.status === 'error' && s.error === 'CREDITS_EXHAUSTED') {
            <button class="cta-button" (click)="openTopUp()">Top up</button>
          } @else if (s.status === 'error' && s.error === 'SUBSCRIPTION_REQUIRED') {
            <button class="cta-button" (click)="openSubscriptionOffer()">
              <span class="cta-label-full">Get discount</span>
              <span class="cta-label-short">Discount</span>
            </button>
          }
          <button
            class="voice-button"
            [matMenuTriggerFor]="voiceMenu"
            (menuOpened)="loadPresets()"
            title="Voice"
            aria-label="Voice"
          >
            @if (voiceAvatarUrl(s.presetId); as avatarUrl) {
              <img
                class="voice-avatar rounded-circle"
                [src]="avatarUrl"
                alt=""
                (error)="onVoiceAvatarError(s.presetId)"
              />
            } @else {
              <i class="ri-user-voice-line"></i>
            }
          </button>
          <span class="panel-divider" aria-hidden="true"></span>
          <!-- No d-flex here: its !important would beat the <=991px display:none that
               collapses these pills into the overflow menu. Display is local, in the SCSS. -->
          <div class="pill-group align-items-center">
            <button
              class="pill-button player-pill"
              [class.active-control]="s.repeat"
              (click)="player.toggleRepeat()"
              title="Repeat"
              aria-label="Repeat"
              [attr.aria-pressed]="s.repeat"
            >
              <app-icon name="repeat" [size]="18" />
            </button>
            @if (s.sleepRemainingSeconds !== null) {
              <button
                class="pill-button player-pill player-pill-wide active-control"
                [matMenuTriggerFor]="sleepMenu"
                title="Sleep timer"
                aria-label="Sleep timer"
              >
                <app-icon name="sleep" [size]="18" />
                <span class="sleep-countdown">{{ formatSleep(s.sleepRemainingSeconds) }}</span>
              </button>
            } @else {
              <button
                class="pill-button player-pill"
                [matMenuTriggerFor]="sleepMenu"
                title="Sleep timer"
                aria-label="Sleep timer"
              >
                <app-icon name="sleep" [size]="18" />
              </button>
            }
            <button
              class="pill-button player-pill"
              [matMenuTriggerFor]="speedMenu"
              title="Playback speed"
              aria-label="Playback speed"
            >
              {{ formatSpeed(s.speed) }}
            </button>
            @if (hasToc(s)) {
              <button
                class="pill-button player-pill"
                [matMenuTriggerFor]="tocMenu"
                title="Table of contents"
                aria-label="Table of contents"
              >
                <app-icon name="list" [size]="18" />
              </button>
            } @else if (s.partsCount > 1) {
              <button
                class="pill-button player-pill"
                [matMenuTriggerFor]="partsMenu"
                title="Parts"
                aria-label="Parts"
              >
                <app-icon name="list" [size]="18" />
              </button>
            }
          </div>
          <span class="panel-divider" aria-hidden="true"></span>
          <!-- Narrow-screen overflow: voice/repeat/sleep/speed exist NOWHERE else in the
               app, so the tiers that hide their pills must still offer them here. -->
          <button
            class="icon-button panel-overflow"
            [matMenuTriggerFor]="overflowMenu"
            title="Playback options"
            aria-label="Playback options"
          >
            <i class="ri-more-2-fill"></i>
          </button>
          <button
            class="pill-button player-pill"
            (click)="player.stop()"
            title="Close player"
            aria-label="Close player"
          >
            <app-icon name="close-x" [size]="18" />
          </button>
        </div>
      </div>

      <!-- iOS "Still there?" prompt after the idle-listening timeout auto-paused playback;
           inline instead of modal so the bar stays usable. -->
      @if (s.idlePaused) {
        <div
          class="idle-notice d-flex flex-wrap align-items-center justify-content-center gap-12 ms-auto me-auto"
          role="status"
        >
          <span class="fs-12 muted-color"
            >Still there? We paused your audio in case you're away.</span
          >
          <button class="pill-button pill-button-primary idle-resume" (click)="player.resume()">
            Continue playing
          </button>
          <button
            class="icon-button"
            (click)="player.dismissIdleNotice()"
            title="Dismiss"
            aria-label="Dismiss"
          >
            <app-icon name="close-x" [size]="16" />
          </button>
        </div>
      }

      <mat-menu #overflowMenu="matMenu" class="app-menu-panel" xPosition="before">
        <button mat-menu-item (click)="player.toggleRepeat()">
          <span [class.fw-semibold]="s.repeat" [class.main-color]="s.repeat">
            Repeat{{ s.repeat ? ' · on' : '' }}
          </span>
        </button>
        <button mat-menu-item [matMenuTriggerFor]="sleepMenu">
          <span
            [class.fw-semibold]="s.sleepRemainingSeconds !== null"
            [class.main-color]="s.sleepRemainingSeconds !== null"
          >
            Sleep timer{{
              s.sleepRemainingSeconds !== null ? ' · ' + formatSleep(s.sleepRemainingSeconds) : ''
            }}
          </span>
        </button>
        <button mat-menu-item [matMenuTriggerFor]="speedMenu">
          Speed · {{ formatSpeed(s.speed) }}
        </button>
        <button mat-menu-item [matMenuTriggerFor]="voiceMenu" (menuOpened)="loadPresets()">
          Voice
        </button>
        @if (hasToc(s)) {
          <button mat-menu-item [matMenuTriggerFor]="tocMenu">Contents</button>
        } @else if (s.partsCount > 1) {
          <button mat-menu-item [matMenuTriggerFor]="partsMenu">
            Parts · {{ s.partIndex + 1 }}/{{ s.partsCount }}
          </button>
        }
      </mat-menu>

      <mat-menu #sleepMenu="matMenu" class="app-menu-panel" xPosition="before">
        <div class="menu-heading">Sleep timer</div>
        @if (s.sleepRemainingSeconds !== null) {
          <button mat-menu-item (click)="player.setSleepTimer(null)">
            <span class="fw-semibold">Turn off</span>
          </button>
        }
        @for (minutes of sleepOptions; track minutes) {
          <button mat-menu-item (click)="player.setSleepTimer(minutes)">
            {{ minutes }} minutes
          </button>
        }
      </mat-menu>

      <mat-menu #speedMenu="matMenu" class="app-menu-panel" xPosition="before">
        @for (option of speedOptions; track option) {
          <button mat-menu-item (click)="player.setSpeed(option)">
            <span [class.fw-semibold]="option === s.speed" [class.main-color]="option === s.speed">
              {{ formatSpeed(option) }}
            </span>
          </button>
        }
      </mat-menu>

      <mat-menu #voiceMenu="matMenu" class="app-menu-panel" xPosition="before">
        @if (presetsLoading) {
          <div class="menu-note muted-color fs-14">Loading voices…</div>
        } @else if (presets) {
          @for (preset of presets; track preset.id) {
            <button mat-menu-item (click)="player.setPreset(preset.id, preset.title)">
              <span class="d-flex align-items-center gap-12 w-100">
                <img class="preset-avatar" [src]="preset.imageUrl" alt="" />
                <span class="d-flex flex-column">
                  <span [class.fw-semibold]="isSelectedPreset(preset)">{{ preset.title }}</span>
                  @if (preset.subtitle) {
                    <span class="fs-12 muted-color">{{ preset.subtitle }}</span>
                  }
                </span>
                @if (isSelectedPreset(preset)) {
                  <i class="ri-check-line main-color ms-auto"></i>
                }
              </span>
            </button>
          }
        } @else {
          <div class="menu-note muted-color fs-14">Voices are unavailable right now</div>
        }
      </mat-menu>

      <mat-menu #tocMenu="matMenu" class="app-menu-panel" xPosition="before">
        @for (row of tocRows(s); track $index) {
          <button
            mat-menu-item
            [disabled]="row.partIndex === null"
            (click)="playTocRow(row)"
            [style.padding-left.px]="16 + row.depth * 16"
          >
            <span
              [class.fw-semibold]="$index === activeTocRowIndex(s)"
              [class.main-color]="$index === activeTocRowIndex(s)"
            >
              {{ row.label }}
            </span>
          </button>
        }
      </mat-menu>

      <mat-menu #partsMenu="matMenu" class="app-menu-panel" xPosition="before">
        @for (part of partNumbers(s); track part) {
          <button mat-menu-item (click)="player.playPart(part, 'parts_menu')">
            <span
              [class.fw-semibold]="part === s.partIndex"
              [class.main-color]="part === s.partIndex"
            >
              Part {{ part + 1 }}
            </span>
          </button>
        }
      </mat-menu>
    </div>
  }
}
